import { useState, useEffect, useCallback, useRef } from 'react';
import { createForm, updateForm, getForm, publishForm as publishFormAPI } from '../services/api/formApi';
import { validateForm, canAutoSave } from '../utils/validation';
import { FORM_STATUS } from '../types/forms';

/**
 * Custom hook for managing form state and operations
 * @param {string} initialFormId - Initial form ID if editing existing form
 * @returns {object} Form management functions and state
 */
export const useForm = (initialFormId = null) => {
  const [form, setForm] = useState({
    id: initialFormId,
    title: '',
    description: '',
    status: FORM_STATUS.DRAFT,
    settings: {
      collectEmail: false,
      allowMultipleResponses: false,
    },
    questions: [],
    createdAt: null,
    publishedAt: null,
    closedAt: null,
  });

  const [loading, setLoading] = useState(!!initialFormId); // Start loading if we have an initialFormId
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [validationResult, setValidationResult] = useState({ isValid: true, errors: [] });

  // Auto-save timeout ref
  const autoSaveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);

  // Load existing form data when initialFormId is provided
  useEffect(() => {
    if (initialFormId) {
      const loadForm = async () => {
        try {
          setLoading(true);
          setLoadError(null);
          const formData = await getForm(initialFormId);
          setForm(formData);
        } catch (err) {
          setLoadError(err.message);
        } finally {
          setLoading(false);
        }
      };

      loadForm();
    }
  }, [initialFormId]);

  // Update form data
  const updateFormData = useCallback((updates) => {
    setForm(prevForm => {
      const newForm = { ...prevForm, ...updates };
      isDirtyRef.current = true;

      // Validate the form
      const validation = validateForm(newForm);
      setValidationResult(validation);

      return newForm;
    });
  }, []);

  // Update form settings
  const updateSettings = useCallback((settings) => {
    updateFormData({ settings: { ...form.settings, ...settings } });
  }, [form.settings, updateFormData]);

  // Add a new question
  const addQuestion = useCallback((questionType, config = {}) => {
    // For checkbox questions, set maxSelections to number of options by default
    if (questionType === 'CHECKBOX' && config.options) {
      config = {
        ...config,
        maxSelections: config.options.length,
      };
    }

    const newQuestion = {
      id: null, // Will be assigned by backend
      type: questionType,
      title: '',
      description: null,
      required: false,
      order: form.questions.length + 1,
      config,
    };

    updateFormData({
      questions: [...form.questions, newQuestion],
    });
  }, [form.questions, updateFormData]);

  // Update a question
  const updateQuestion = useCallback((questionIndex, updates) => {
    const updatedQuestions = [...form.questions];
    updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], ...updates };

    updateFormData({ questions: updatedQuestions });
  }, [form.questions, updateFormData]);

  // Remove a question
  const removeQuestion = useCallback((questionIndex) => {
    const updatedQuestions = form.questions.filter((_, index) => index !== questionIndex);
    // Reorder remaining questions
    const reorderedQuestions = updatedQuestions.map((question, index) => ({
      ...question,
      order: index + 1,
    }));

    updateFormData({ questions: reorderedQuestions });
  }, [form.questions, updateFormData]);

  // Move question up/down
  const moveQuestion = useCallback((questionIndex, direction) => {
    const newIndex = questionIndex + direction;
    if (newIndex < 0 || newIndex >= form.questions.length) return;

    const updatedQuestions = [...form.questions];
    [updatedQuestions[questionIndex], updatedQuestions[newIndex]] =
      [updatedQuestions[newIndex], updatedQuestions[questionIndex]];

    // Update order numbers
    updatedQuestions.forEach((question, index) => {
      question.order = index + 1;
    });

    updateFormData({ questions: updatedQuestions });
  }, [form.questions, updateFormData]);

  // Save form (create or update)
  const saveForm = useCallback(async (isDraft = true) => {
    if (!canAutoSave(form)) return;

    setSaving(true);
    setError(null);

    try {
      let result;
      if (!form.id) {
        // Create new form
        const createData = {
          title: form.title,
          description: form.description,
          settings: form.settings,
          questions: [], // Start with empty questions for creation
        };
        result = await createForm(createData);

        // Update local form with returned data
        setForm(prevForm => ({
          ...prevForm,
          id: result.id,
          createdAt: result.createdAt,
          status: result.status,
        }));
      } else {
        // Update existing form
        result = await updateForm(form.id, {
          title: form.title,
          description: form.description,
          settings: form.settings,
          questions: form.questions,
        });

        // Update local form with returned data (including updated question IDs)
        setForm(prevForm => ({
          ...prevForm,
          ...result,
        }));
      }

      setLastSaved(new Date());
      isDirtyRef.current = false;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [form]);

  // Auto-save effect
  useEffect(() => {
    if (isDirtyRef.current && canAutoSave(form) && validationResult.isValid) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for auto-save (5 seconds)
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveForm(true).catch(console.error);
      }, 5000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form, validationResult.isValid, saveForm]);

  // Manual save function
  const manualSave = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    await saveForm(true);
  }, [saveForm]);

  // Publish form
  const publishForm = useCallback(async () => {
    if (!form.id) {
      throw new Error('Cannot publish unsaved form');
    }

    // Validate before publishing
    const validation = validateForm(form);
    if (!validation.isValid) {
      throw new Error('Form has validation errors. Please fix them before publishing.');
    }

    setLoading(true);
    setError(null);
    try {
      // First save as draft
      await saveForm(true);

      // Then publish via API
      await publishFormAPI(form.id);

      // Update local form status to published
      setForm(prevForm => ({
        ...prevForm,
        status: FORM_STATUS.PUBLISHED,
        publishedAt: new Date(),
      }));

      return true; // Return success indicator
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [form, saveForm]);

  return {
    form,
    loading,
    saving,
    error,
    loadError,
    lastSaved,
    validationResult,
    updateFormData,
    updateSettings,
    addQuestion,
    updateQuestion,
    removeQuestion,
    moveQuestion,
    saveForm: manualSave,
    publishForm,
  };
};
